import React from 'react';
import cs from 'classnames';
import orderBy from 'lodash/orderBy';
import { ConfigStore } from '../../stores/ConfigStore';
import { injectV2 } from '../../injectV2';
import {
  DEFAULT_IMAGE_TYPES,
  getImageExtensionFromType,
  getImageTypeFromExtension,
  ImageExtension,
  ImageMime,
} from '../../../utils/imageTypes';
import { pictureDataHooks } from './Picture.dataHooks';
import { Image } from './Image/Image';
import s from './Picture.scss';

function getImageFilenameAndExtension(
  imagePath,
): {
  filename: string;
  extension: string;
} {
  const [filenameWithExtension] = imagePath.split('/').reverse();
  const [filename, extension] = filenameWithExtension.split('.');

  return { filename, extension };
}

function getImageTypeFromPath(imagePath: string): ImageMime {
  const { extension } = getImageFilenameAndExtension(imagePath);

  return getImageTypeFromExtension(extension as ImageExtension);
}

interface BreakpointConfig {
  maxScreenWidth: number;
  imageWidth: number;
  densityList: number[];
}

interface Breakpoint {
  maxScreenWidth: number;
  densityList: {
    imageWidth: number;
    imageHeight: number;
    density: number;
  }[];
}

interface ImageSize {
  width: number;
  height: number;
}

interface PictureProps {
  imagePath: string;
  categorySlug?: string | 'search' | null;
  subCategorySlug?: string | null;
  sourceImageSize: ImageSize;
  breakpoints: Breakpoint[];
  filters?: string;
  dataHook?: string;
  alt?: string;
  onLoad?: () => void;
  onLoadStart?: () => void;
  biOrigin?: string;
  biElement?: string;
  biElementValue?: string;
  configStore: ConfigStore;
  imageClassName?: string;
  imageTypes?: ImageMime[];
}

interface Source {
  screenWidth: number;
  densityList: {
    src: string;
    imageWidth: number;
    imageHeight: number;
    density: number;
  }[];
  type: ImageMime;
}

interface ImageURLProps {
  imagePath: string;
  width: number;
  height: number;
  type: ImageMime;
  filters: string;
  imagesDomain: string;
}

const DEFAULT_IMAGE_FILTER = 'q_90,usm_0.60_1.00_0.01';

export const createBreakpoints = (breakpoints: BreakpointConfig[], aspectRatio: ImageSize): Breakpoint[] =>
  breakpoints.map((breakpoint) => ({
    maxScreenWidth: breakpoint.maxScreenWidth,
    densityList: breakpoint.densityList.map((density) => {
      const imageWidth = Math.ceil(breakpoint.imageWidth * density);
      const imageHeight = Math.ceil(imageWidth * (aspectRatio.height / aspectRatio.width));

      return {
        imageWidth,
        imageHeight,
        density,
      };
    }),
  }));

function getImageUrl({ type, imagePath, imagesDomain, width, height, filters }: ImageURLProps): string {
  const extension = getImageExtensionFromType(type);
  const { filename } = getImageFilenameAndExtension(imagePath);

  return `//${imagesDomain}${imagePath}/v1/fill/w_${width},h_${height},${filters}/${filename}.${extension}`;
}

const PictureCmp: React.FC<PictureProps> = ({
  configStore,
  imagePath,
  breakpoints,
  sourceImageSize,
  dataHook,
  alt,
  onLoad,
  onLoadStart,
  biOrigin,
  biElement,
  biElementValue,
  filters = DEFAULT_IMAGE_FILTER,
  imageClassName,
  imageTypes = DEFAULT_IMAGE_TYPES,
  categorySlug,
  subCategorySlug,
}) => {
  const { width: sourceWidth, height: sourceHeight } = sourceImageSize;
  const imagesDomain = configStore.config.imagesMediaPlatformDomain;

  const sources: Source[] = [].concat(
    ...imageTypes.map((type) =>
      breakpoints.map((breakpoint) => ({
        screenWidth: breakpoint.maxScreenWidth,
        densityList: breakpoint.densityList.map(({ density, imageWidth, imageHeight }) => ({
          src: getImageUrl({ imagesDomain, imagePath, width: imageWidth, height: imageHeight, type, filters }),
          imageWidth,
          imageHeight,
          density,
        })),
        type,
      })),
    ),
  );

  const baseImageType = getImageTypeFromPath(imagePath);
  const baseSource = sources.find((source) => source.type === baseImageType) ?? sources[0];
  const baseImage = baseSource.densityList[0];

  const getSources = (): {
    key: string;
    media: string;
    srcSet: string;
    type: ImageMime;
  }[] => {
    const originalMedia = `(min-width: ${sources[sources.length - 1].screenWidth + 1}px)`;
    const filters = 'al_c,q_90';
    const originalSources = imageTypes.map((type) => {
      return {
        key: `original-${type}`,
        media: originalMedia,
        srcSet: getImageUrl({ imagesDomain, imagePath, width: sourceWidth, height: sourceHeight, type, filters }),
        type,
      };
    });

    const result = sources
      .map((source) => ({
        key: `${source.screenWidth}-${source.type}`,
        media: `(max-width: ${source.screenWidth}px)`,
        srcSet: source.densityList.map(({ src, density }) => `${src} ${density}x`).join(', '),
        type: source.type,
      }))
      .concat(originalSources);

    return orderBy(result, ['type'], ['desc']);
  };

  return (
    <picture
      data-hook={dataHook}
      data-bi-origin={biOrigin}
      data-bi-element={biElement}
      data-bi-element-value={biElementValue}
      data-bi-element-category={categorySlug}
      data-bi-element-subcategory={subCategorySlug}
    >
      {getSources().map((sourceProps) => (
        <source data-hook={pictureDataHooks.source()} {...sourceProps} />
      ))}
      <Image
        className={cs(s.image, imageClassName)}
        src={baseImage.src}
        alt={alt}
        dataHook={pictureDataHooks.image()}
        onLoad={onLoad}
        onLoadStart={onLoadStart}
      />
    </picture>
  );
};

export const Picture = injectV2('configStore')(PictureCmp);
